import { Technique } from "domain/entities/techniques.entities";
import { selectTechniques } from "domain/techniques/techniques.selectors";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "ui/hook/store";
import { translate } from "ui/i18n";
import { PageHeader } from "ui/pages/app/components/PageHeader";
import { TechniqueCard } from "ui/pages/app/components/activities/techniques/TechniqueCard";
import { selectSeances } from "domain/seances/seances.selectors";
import { SeanceCard } from "ui/pages/app/components/activities/seances/SeanceCard";
import { Seance } from "domain/entities/seances.entities";
import { selectIsPremium } from "domain/auth/auth.selectors";
import { Snackbar } from "ui/pages/snackbar";
import { HiDocument, HiFolderOpen, HiPhotograph, HiPlay } from "react-icons/hi";
import { EmptyFavorites } from "ui/assets/svg/EmptyFavorites";
import { TutorialCard } from "ui/pages/app/components/activities/tutorials/TutorialCard";
import { selectTutorials } from "domain/tutorials/tutorials.selectors";
import { Tutorial } from "domain/entities/tutorials.entities";
import { selectVisualSupports } from "domain/visual-supports/visual-supports.selectors";
import { VisualSupportCard } from "ui/pages/app/components/activities/visual-supports/VisualSupportCard";
import { VisualSupport } from "domain/entities/visual-supports.entities";

export function Favorites() {
  const techniques = useAppSelector(selectTechniques);
  const seances = useAppSelector(selectSeances);
  const tutorials = useAppSelector(selectTutorials);
  const supports = useAppSelector(selectVisualSupports);
  const isPremium = useAppSelector(selectIsPremium);
  const navigate = useNavigate();

  const handleTechniqueCardClick = async (technique: Technique) => {
    if (!isPremium && !!technique.premium) {
      await Snackbar.removeSubscriptionAlert();
      Snackbar.showSubscriptionAlert(() => navigate("/account"), "technique");
    } else navigate(`/techniques?id=${technique?.id}`);
  };

  const handleSeanceCardClick = async (seance: Seance) => {
    if (!isPremium && !!seance.premium) {
      await Snackbar.removeSubscriptionAlert();
      Snackbar.showSubscriptionAlert(() => navigate("/account"), "seance");
    } else navigate(`/seances?id=${seance?.id}`);
  };

  const handleSupportCardClick = async (support: VisualSupport) => {
    if (!isPremium && !!support.premium) {
      await Snackbar.removeSubscriptionAlert();
      Snackbar.showSubscriptionAlert(
        () => navigate("/account"),
        "visual-support"
      );
    } else navigate(`/visual-supports?id=${support?.id}`);
  };

  const handleTutorialCardClick = async (tutorial: Tutorial) => {
    if (!isPremium && !!tutorial.premium) {
      await Snackbar.removeSubscriptionAlert();
      Snackbar.showSubscriptionAlert(() => navigate("/account"), "tutorial");
    } else navigate(`/tutorials?id=${tutorial?.id}`);
  };

  return (
    <div className={`flex h-full w-full flex-row overflow-x-hidden`}>
      <div className="h-full w-full px-2 pt-2 md:px-12 md:pt-8">
        <PageHeader title={translate("ns1:Routes.Favorites")} />

        {techniques?.length === 0 && seances?.length === 0 ? (
          <div className="mt-[80px] flex flex-1 flex-col items-center text-center text-base text-darkGrey-700 md:mt-[120px]">
            <p className="font-bold">
              {translate("ns1:Favorites.NoActivityFavorite")}
            </p>
            <p className="mt-4 max-w-[400px] font-medium">
              {translate("ns1:Favorites.ClickOnHeart")}
            </p>
            <div className="mt-16 w-full max-w-[500px]">
              <EmptyFavorites />
            </div>
          </div>
        ) : (
          <>
            {/* Favorite Techniques */}
            <h2 className="mt-10 ps-5 text-lg text-secondaryGrey-900 md:text-2xl">
              {translate("ns1:Favorites.FavoriteTechniques")}
            </h2>
            {techniques && techniques.length > 0 ? (
              <div className="mb-8 mt-4 grid w-full grid-cols-2 gap-2 md:mt-8 md:gap-6 lg:grid-cols-3 xl:grid-cols-4">
                {techniques.map((technique, index) => (
                  <TechniqueCard
                    technique={technique}
                    key={"technique" + index}
                    onClick={() => handleTechniqueCardClick(technique)}
                    disableFlip
                  />
                ))}
              </div>
            ) : (
              <EmptyState type="technique" />
            )}

            {/* Favorite Seances */}
            <h2 className="mt-10 ps-5 text-lg text-secondaryGrey-900 md:text-2xl">
              {translate("ns1:Favorites.FavoriteSeances")}
            </h2>
            {seances && seances.length > 0 ? (
              <div className="mb-8 mt-4 grid w-full grid-cols-2 gap-2 md:mt-8 md:gap-6 lg:grid-cols-3 xl:grid-cols-4">
                {seances.map((seance, index) => (
                  <SeanceCard
                    seance={seance}
                    key={"seance" + index}
                    onClick={() => handleSeanceCardClick(seance)}
                  />
                ))}
              </div>
            ) : (
              <EmptyState type="seance" />
            )}

            {/* Favorite Visual Supports */}
            <h2 className="mt-10 ps-5 text-lg text-secondaryGrey-900 md:text-2xl">
              {translate("ns1:Favorites.FavoriteVisualSupports")}
            </h2>
            {supports && supports.length > 0 ? (
              <div className="mb-8 mt-4 grid w-full grid-cols-2 gap-2 md:mt-8 md:gap-6 lg:grid-cols-3 xl:grid-cols-4">
                {supports.map((support, index) => (
                  <VisualSupportCard
                    support={support}
                    key={"support" + index}
                    onClick={() => handleSupportCardClick(support)}
                  />
                ))}
              </div>
            ) : (
              <EmptyState type="support" />
            )}

            {/* Favorite Tutorials */}
            <h2 className="mt-10 ps-5 text-lg text-secondaryGrey-900 md:text-2xl">
              {translate("ns1:Favorites.FavoriteTutorials")}
            </h2>
            {tutorials && tutorials.length > 0 ? (
              <div className="mb-8 mt-4 grid w-full grid-cols-2 gap-2 md:mt-8 md:gap-6 lg:grid-cols-3 xl:grid-cols-4">
                {tutorials.map((tutorial, index) => (
                  <TutorialCard
                    tutorial={tutorial}
                    key={"tutorial" + index}
                    onClick={() => handleTutorialCardClick(tutorial)}
                  />
                ))}
              </div>
            ) : (
              <EmptyState type="tutorial" />
            )}
          </>
        )}
      </div>
    </div>
  );
}

const EmptyState = ({
  type,
}: {
  type: "technique" | "seance" | "tutorial" | "support";
}) => {
  return (
    <div className="mb-8 mt-4 flex w-fit items-center space-x-4 rounded-[20px] border-2 border-darkGrey-200 px-4 py-5 md:mt-8">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-darkGrey-200">
        {type === "technique" && <HiDocument size={24} color="#2B3674" />}
        {type === "seance" && <HiFolderOpen size={24} color="#2B3674" />}
        {type === "tutorial" && <HiPlay size={24} color="#2B3674" />}
        {type === "support" && <HiPhotograph size={24} color="#2B3674" />}
      </div>
      <span className="text-sm font-medium text-darkGrey-700">
        {translate(`ns1:Favorites.NoFavorite${type}`)}
      </span>
    </div>
  );
};
